import { get, find, has } from "lodash";
import {
  RaceProgram,
  RaceWagerType,
  BettingInterest,
  RaceInfoMyBets
} from "@tvg/ts-types/Race";
import {
  BIRunner,
  BettingInterests,
  VisualSelections
} from "@tvg/ts-types/Selections";
import { BetRunnerDetails, BetSelection } from "@tvg/ts-types/Bet";
import formatSequential from "@tvg/formatter/numeric";
import {
  WagerTypeCodesEnum,
  WagerTypes,
  WagerTypesGroup
} from "@tvg/ts-types/Wager";

export const simpleBetTypes = [
  WagerTypeCodesEnum.WIN,
  WagerTypeCodesEnum.PLACE,
  WagerTypeCodesEnum.SHOW,
  WagerTypeCodesEnum.WIN_PLACE,
  WagerTypeCodesEnum.WIN_SHOW,
  WagerTypeCodesEnum.PLACE_SHOW,
  WagerTypeCodesEnum.WIN_PLACE_SHOW
];

const setMatrixSizeByColumnCount = (columnCount: number): string[][] =>
  columnCount >= 1 ? Array(columnCount).fill([]) : [[]];

const hasBetType = (
  betTypeID: number | null | undefined,
  currentRace: RaceProgram
): boolean => {
  const wagerTypes = get(currentRace, "wagerTypes", []);
  return !!find(
    wagerTypes,
    (wagerType) => `${wagerType.type.id}` === `${betTypeID}`
  );
};

const getDefaultBetType = (
  currentRace: RaceProgram,
  defaultBetTypeID: string = "",
  logged: boolean = false
): RaceWagerType | null => {
  const wagerTypes = get(currentRace, "wagerTypes", []);
  const defaultType = get(wagerTypes, "[0]");

  if (logged) {
    const findWagerType = find(
      wagerTypes,
      (wagerType) => `${wagerType.type.id}` === `${defaultBetTypeID}`
    );

    const isSpecialGroup = WagerTypesGroup.WIN_PLACE_SHOW.includes(
      Number(defaultBetTypeID)
    );

    const isSpecialGroupAvailableOnRace = wagerTypes.some(
      (wagerType) => wagerType.type.id === WagerTypes.WIN_PLACE_SHOW
    );

    const WPSDefaultBetType = wagerTypes.find(
      (wagerType) => wagerType.type.id === WagerTypes.WIN_PLACE_SHOW
    );

    const handleDefaultBetType =
      isSpecialGroup && isSpecialGroupAvailableOnRace
        ? WPSDefaultBetType
        : defaultType;

    return findWagerType || (handleDefaultBetType as RaceWagerType);
  }

  return defaultType;
};

const isValidBetType = (
  currentRace: RaceProgram,
  betType: number | null | undefined
) => hasBetType(betType, currentRace);

const isValidBetAmount = (
  wager: RaceWagerType | null | undefined,
  betAmount = 0
) => betAmount >= get(wager, "minWagerAmount", 100000);

const isValidBetSelection = (
  wager: RaceWagerType | null | undefined,
  betSelections: string[][]
) => betSelections.length === get(wager, "columnCount", 1);

export const getVisualSelections = ({
  selections,
  bettingInterests,
  isLegSelections,
  shouldShowAlternates
}: {
  selections: Array<Array<string>>;
  bettingInterests?: BettingInterest[];
  isLegSelections?: boolean;
  shouldShowAlternates?: boolean;
}): VisualSelections[] =>
  (selections || []).map((selection, selectionIndex) =>
    (selection || []).flatMap((singleSelection) => {
      const isCurrentRaceSelections = selectionIndex === 0 || !isLegSelections;
      const biRunners: VisualSelections = [];

      // Remove 'B' from alternate selection for matching purposes
      const cleanedSelection = singleSelection.replace("B", "");
      const isAlternate = singleSelection.includes("B");

      if (
        typeof shouldShowAlternates !== "undefined" &&
        !shouldShowAlternates &&
        isAlternate
      ) {
        return [];
      }
      const selectedBI = (bettingInterests || []).find(
        (bi) => +bi.biNumber === +cleanedSelection
      );

      if (selectedBI && isCurrentRaceSelections) {
        (selectedBI.runners || []).forEach((runner) => {
          biRunners.push({
            number: runner.runnerId,
            numberColor: selectedBI.numberColor,
            saddleColor: selectedBI.saddleColor,
            isAlternate,
            ...(isCurrentRaceSelections
              ? { scratched: !!runner.scratched }
              : {})
          });
        });
      } else {
        biRunners.push({
          number: cleanedSelection,
          numberColor: "",
          saddleColor: "",
          isAlternate
        });
      }

      return biRunners;
    })
  );

export const getVisualSelectionsWithRaces = (
  selections: Array<Array<string>>,
  races: Array<RaceProgram>,
  isPick: boolean = false
): VisualSelections[] =>
  (selections || []).map((raceSelections, index) => {
    const currentRace = get(races, isPick ? index : 0, []);
    const biRunners: VisualSelections = [];

    (raceSelections || []).forEach((runnerSelection) => {
      const bettingInterests: Array<BettingInterest> = get(
        currentRace,
        "bettingInterests",
        []
      );
      const selectedBI = bettingInterests.find(
        (bi) => +bi.biNumber === +runnerSelection
      );

      const runners = get(selectedBI, "runners", []);
      if (!runners.length) {
        biRunners.push({
          number: runnerSelection,
          numberColor: "",
          saddleColor: "",
          scratched: false
        });
      } else {
        runners.forEach((runner) => {
          biRunners.push({
            number: runner.runnerId,
            numberColor: selectedBI?.numberColor || "",
            saddleColor: selectedBI?.saddleColor || "",
            scratched: !!runner.scratched
          });
        });
      }
    });
    return biRunners;
  });

export const getSelectionsRunnerNbrScratched = (
  selections: Array<VisualSelections>
): Array<Array<string>> =>
  selections.map((fullSelection) =>
    fullSelection
      .filter((selection) => selection.scratched)
      .map((scratched) => scratched.number)
  );

export const getValidRunnerId = (
  selection: Array<BetSelection | BettingInterests>
) =>
  selection.reduce((accSelection: string[], selectionRunner) => {
    const validSelection = (
      selectionRunner.runners as Array<BetRunnerDetails | BIRunner>
    ).reduce((accRunner: string[], runner) => {
      const isScrached = has(runner, "isScratched")
        ? get(runner, "isScratched", false)
        : get(runner, "scratched", false);

      if (!isScrached) {
        accRunner.push(runner?.runnerId?.toString());
      }

      return accRunner;
    }, [] as string[]);

    const isAlternate = get(selectionRunner, "isAlternate", false);

    if (validSelection.length && !isAlternate) {
      accSelection.push(...validSelection);
    }

    return accSelection;
  }, []);

const getBetAmount = (
  wager: RaceWagerType | null,
  userAccountNumber: string,
  MinimalValues: boolean,
  BetAmountPreference: Record<string, number>
) => {
  const picksDefault = [310, 330, 340, 350, 360, 370, 380, 390, 400];
  if (!!userAccountNumber && !MinimalValues) {
    const betTypeId = +get(wager, "type.id", "");

    let defaultValue = 0;

    // Identify picks and daily doubles
    if (picksDefault.includes(betTypeId)) {
      defaultValue = +get(BetAmountPreference, "P", 0);
    } else {
      defaultValue = +get(BetAmountPreference, betTypeId, 0);
    }

    return defaultValue >= get(wager, "minWagerAmount", 100000)
      ? defaultValue
      : get(wager, "wagerAmounts[0]", 0);
  }
  return get(wager, "wagerAmounts[0]", 0);
};

const getBetAmountWPSBetSelections = (
  wager: RaceWagerType | undefined,
  BetAmountPreference: Record<string, number>
) => {
  const betTypeId = +get(wager, "type.id", "");
  const defaultValue = +get(BetAmountPreference, betTypeId, 0);

  return defaultValue >= get(wager, "minWagerAmount", 100000)
    ? defaultValue
    : get(wager, "wagerAmounts[0]", 0);
};

const filterBetTypeFromRaceCallback =
  (betTypeID: number | null | undefined) => (wagerType: RaceWagerType) =>
    `${wagerType.type.id}` === `${betTypeID}`;

const getBetTypeFromRace = (
  currentRace: RaceProgram,
  betTypeID: number | null | undefined,
  useSubTypes: boolean = false
): RaceWagerType => {
  const wagerTypes: Array<RaceWagerType> = get(currentRace, "wagerTypes", []);
  const findWagerType = find(
    wagerTypes,
    filterBetTypeFromRaceCallback(betTypeID)
  );

  if (useSubTypes) {
    const subWagerTypes = wagerTypes.reduce((acc, wagerType) => {
      if (wagerType?.types?.length) {
        acc.push(...wagerType.types);
      }

      return acc;
    }, [] as RaceWagerType[]);

    const findSubWagerType = find(
      subWagerTypes,
      filterBetTypeFromRaceCallback(betTypeID)
    );

    return findSubWagerType || findWagerType || wagerTypes[0];
  }

  return findWagerType || wagerTypes[0];
};

const getOriginalBetTypeFromRace = (
  currentRace: RaceProgram | RaceInfoMyBets,
  betTypeID?: WagerTypeCodesEnum
): RaceWagerType | undefined => {
  let originalWagerType: RaceWagerType | undefined;

  const searchWagerType = get(currentRace, "wagerTypes", []).find(
    (wt: RaceWagerType) => {
      if (
        wt.type.code !== betTypeID &&
        wt.type.id !== parseInt(`${betTypeID}`, 10)
      ) {
        // original bet type from url ( not grouped)
        originalWagerType = wt.types?.find(
          (wtIn: RaceWagerType) =>
            wtIn.type.code === betTypeID ||
            wtIn.type.id === parseInt(`${betTypeID}`, 10)
        );
        return !!originalWagerType;
      }
      return true;
    }
  );
  return originalWagerType || searchWagerType;
};

export const getWagerTypesNames = (
  wagerType?: RaceWagerType,
  enableAlternateSelection?: boolean
) => {
  if (!wagerType) {
    return [];
  }

  const {
    isBox,
    columnCount,
    isKey,
    isWheel,
    legCount,
    type: { code }
  } = wagerType;

  if (enableAlternateSelection) {
    return ["Select", "Alternate"];
  }

  if (isKey && isBox) {
    return ["Key", "Box"];
  }

  if (isKey) {
    return ["Key", "Select"];
  }

  if (isBox) {
    return ["Box"];
  }

  if (isWheel) {
    return ["Primary", "Others"];
  }

  if (simpleBetTypes.includes(code) && legCount === 1) {
    return wagerType?.type.name.split("/") || [];
  }
  if ((columnCount === 1 && !isBox) || legCount > 1) {
    return ["Select"];
  }

  return Array.from({ length: columnCount }, (_, i) => formatSequential(i + 1));
};

const betSelections = {
  getDefaultBetType,
  setMatrixSizeByColumnCount,
  hasBetType,
  getBetTypeFromRace,
  getBetAmount,
  getBetAmountWPSBetSelections,
  isValidBetType,
  isValidBetAmount,
  isValidBetSelection,
  getVisualSelections,
  getVisualSelectionsWithRaces,
  getSelectionsRunnerNbrScratched,
  getWagerTypesNames,
  getOriginalBetTypeFromRace
};

export default betSelections;
